import React, { useCallback, memo } from 'react';
import { ImageIcon, Film, Mic2, Type, Music, FileText, Upload, MonitorUp, Video, AlignLeft, UserRound, AudioWaveform, ShoppingBag, Mic, SquareStack } from 'lucide-react';
import { NodeType } from '@/types';

interface TryActionItem {
  id: string;
  label: string;
  icon: React.ElementType;
  mode?: string;
}

const TRY_ACTIONS: Record<NodeType, TryActionItem[]> = {
  [NodeType.IMAGE_GENERATOR]: [
    { id: 'img2img', label: '图生图', icon: ImageIcon },
    { id: 'upscale', label: '图片高清', icon: MonitorUp },
  ],
  [NodeType.VIDEO_GENERATOR]: [
    { id: 'first_last_frame', label: '首尾帧生成视频', icon: Video },
    { id: 'first_frame', label: '首帧生成视频', icon: Film },
  ],
  [NodeType.AUDIO_GENERATOR]: [
    { id: 'audio_to_video', label: '音频生视频', icon: Film },
  ],
  [NodeType.PROMPT_INPUT]: [
    { id: 'write_content', label: '自己编写内容', icon: FileText },
    { id: 'txt2video', label: '文生视频', icon: Video },
    { id: 'img2prompt', label: '图片反推提示词', icon: ImageIcon },
    { id: 'txt2music', label: '文字生音乐', icon: Music },
  ],
  [NodeType.MEDIA_UPLOAD]: [],
  [NodeType.IMAGE_EDITOR]: [],
  [NodeType.TEXT_NODE]: [],
  [NodeType.SCRIPT_NODE]: [
    { id: 'script_to_shots', label: '剧本生成分镜脚本', icon: AlignLeft },
    { id: 'character_to_shots', label: '角色生成分镜脚本', icon: UserRound },
    { id: 'manual_write', label: '自己编写分镜脚本', icon: FileText },
  ],
  // 智能剪辑四大模板入口（方案 §4.1.1 定稿）：点击后打开全屏面板并预置对应模式
  [NodeType.VIDEO_COMPOSE]: [
    { id: 'explain', label: '讲解视频', icon: AudioWaveform, mode: 'explain' },
    { id: 'ads', label: '批量广告', icon: ShoppingBag, mode: 'ads' },
    { id: 'talking', label: '口播视频', icon: Mic, mode: 'talking' },
    { id: 'remix', label: '素材混剪', icon: SquareStack, mode: 'remix' },
  ],
  [NodeType.PANORAMA_GENERATOR]: [],
  [NodeType.DIRECTOR]: [],
  [NodeType.DETAIL_PAGE]: [],
  [NodeType.LAYER_EDIT]: [],
  [NodeType.GROUP]: [],
};

interface TryActionsProps {
  nodeId: string;
  nodeType: NodeType;
  onTryAction?: (actionId: string) => void;
}

const TryActionsInner: React.FC<TryActionsProps> = ({
  nodeId,
  nodeType,
  onTryAction,
}) => {
  const actions = TRY_ACTIONS[nodeType] || [];

  const handleActionClick = useCallback((action: TryActionItem) => {
    if (onTryAction) {
      onTryAction(action.id);
    }
  }, [onTryAction]);

  if (actions.length === 0) return null;

  return (
    <div className="absolute bottom-4 left-4 flex flex-col gap-2">
      <span className="text-[30px] text-white font-medium">尝试：</span>
      <div className="flex flex-col gap-1.5">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              onClick={() => handleActionClick(action)}
              className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/5 transition-colors group/action"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Icon size={25} className="text-white group-hover/action:text-white" />
              <span className="text-[25px] text-white group-hover/action:text-white transition-colors">
                {action.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TryActions = memo(TryActionsInner, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType
  );
});
